import React from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { Link } from 'gatsby'
import Layout from '../components/layout_contact'
import SEO from '../components/seo'

const PERSON_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://www.davidlwaldronarchitect.com/about/#person",
  "name": "David L. Waldron",
  "jobTitle": "Architect",
  "url": "https://www.davidlwaldronarchitect.com/about/",
  "image": "https://www.davidlwaldronarchitect.com/images/david-waldron.png",
  "description": "Licensed architect in Friday Harbor, San Juan Island, Washington. Worked as an architect since 1968 and in independent practice on San Juan Island since 1980. Specializes in residential, commercial, historic preservation, and passive solar design.",
  "alumniOf": [
    {
      "@type": "CollegeOrUniversity",
      "name": "University of Washington",
      "department": "School of Architecture",
      "description": "Attended"
    },
    {
      "@type": "CollegeOrUniversity",
      "name": "Willamette University"
    }
  ],
  "worksFor": {
    "@id": "https://www.davidlwaldronarchitect.com/#business"
  },
  "hasOccupation": {
    "@type": "Occupation",
    "name": "Architect",
    "occupationalCategory": "17-1011"
  },
  "award": [
    "American Legion Leadership Award (2013)",
    "BSA Silver Beaver Award"
  ],
  "workLocation": {
    "@type": "Place",
    "name": "Friday Harbor, San Juan Island, Washington",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "310 Spring Street",
      "addressLocality": "Friday Harbor",
      "addressRegion": "WA",
      "postalCode": "98250",
      "addressCountry": "US"
    }
  },
  "knowsAbout": [
    "Residential Architecture",
    "Commercial Architecture",
    "Historic Preservation",
    "Passive Solar Design",
    "Affordable Housing Design"
  ],
  "memberOf": [
    {
      "@type": "Organization",
      "name": "Spring Street International School Board of Directors"
    },
    {
      "@type": "Organization",
      "name": "Friday Harbor Historic Preservation Review Board"
    }
  ]
}

const AboutPage = () => (
  <Layout pageInfo={{ pageName: "about", pageTitle: "ABOUT" }}>
    <SEO
      title="About David L. Waldron"
      description="David L. Waldron has worked as an architect since 1968 and has been in independent practice in Friday Harbor on San Juan Island, Washington since 1980. Specializing in residential, commercial, and historic preservation architecture."
      keywords={[`architect`, `Friday Harbor`, `San Juan Island`, `David Waldron`, `biography`, `residential architecture`, `historic preservation`]}
      pathname="/about/"
    />
    <Container className="mt-4 mb-4" style={{maxWidth: '800px'}}>
      <Row>
        <Col md={4} className="text-center mb-3">
          <img
            src="/images/david-waldron.png"
            alt="David L. Waldron, architect in Friday Harbor, San Juan Island, Washington"
            style={{maxWidth: '200px', width: '100%'}}
          />
        </Col>
        <Col md={8}>
          <h1 style={{fontSize: '1.5rem'}}>David L. Waldron</h1>
          <p>
            David L. Waldron is a licensed architect based in Friday Harbor on San Juan Island, Washington.
            He has worked as an architect since 1968 and has been in independent practice on San Juan
            Island since 1980, making him one of the longest-established architects in the San Juan Islands.
          </p>
          <p>
            David grew up in the Seattle area and studied at Willamette University before attending the
            University of Washington School of Architecture. He moved to San Juan Island in 1980 and has
            been designing homes, commercial buildings, and community spaces on the island ever since.
          </p>
        </Col>
      </Row>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Early Career</h2>
      <p>
        Before opening his own practice on San Juan Island, David spent a decade working for
        two established Seattle firms:
      </p>
      <ul>
        <li>
          <strong>1968&ndash;1973 &middot; Fred Bassetti and Co.</strong> &mdash; Contributed to the
          design of the underwater dome at the Seattle Aquarium and the Nocturnal Mammal exhibit
          at the Woodland Park Zoo.
        </li>
        <li>
          <strong>1973&ndash;1978 &middot; James F. Hamilton, Architect</strong> &mdash; Designed
          Eddie Bauer retail stores across the United States and Canada.
        </li>
      </ul>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Specialties</h2>
      <p>
        David's work spans new residential design, residential remodels, commercial architecture,
        historic preservation and restoration, passive solar design, and affordable housing.
        His designs often incorporate passive solar principles and local materials suited to
        the San Juan Islands environment.
      </p>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Selected Projects</h2>
      <ul>
        <li>
          <Link to="/new_residential/Eagles_Nest_Estate">Eagles Nest Estate</Link> — 15,000 sq. ft.,
          50 rooms on 3 levels, stucco and stone veneer with slate roofing and copper details
        </li>
        <li>
          <Link to="/commercial/Spring_Street_International_School">Spring Street International School</Link> — classroom
          building, studio building, and 20-student dormitory for an experiential boarding school in downtown Friday Harbor
        </li>
        <li>
          <Link to="/commercial/Brickworks">Brickworks Event Center</Link> — conceptual plans for restoring
          a 1921 concrete block factory into a community center and farmers market
        </li>
        <li>
          Friday Harbor Food Bank — designed expansion and exterior restoration (2022), adding storage,
          customer service space, and weather protection
        </li>
        <li>
          <Link to="/commercial/Memorial Park">Memorial Park</Link> — expansion and redesign of the
          historic park around a 1921 monument at the shoreline end of Friday Harbor's main street
        </li>
        <li>
          <Link to="/in_design/Vic's_Drive_Inn">Vic's Drive Inn</Link> — expansion of the beloved 1958
          Friday Harbor gathering spot with a 19-room hotel in 1950s architectural style
        </li>
      </ul>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Pro Bono &amp; Community Design</h2>
      <p>
        A significant portion of David's work on San Juan Island has been donated to the
        community. Pro bono projects include:
      </p>
      <ul>
        <li>
          Four buildings at <a href="https://www.springstreet.org/" target="_blank" rel="noopener noreferrer">Spring Street International School</a> totaling
          roughly $2.5 million in design work &mdash; a 20-student dormitory, Art Studio building,
          Classroom Building, and Commons multi-purpose building
        </li>
        <li>Additions to the Friday Harbor Food Bank</li>
        <li>Brickworks building and plaza</li>
        <li>San Juan Historical Museum conceptual studies</li>
        <li>Sakya Society conceptual designs</li>
        <li>Alchemy Art Center designs</li>
        <li>Illustrations for the Friday Harbor Historic Preservation Review Board Manual</li>
        <li>Conceptual design for Vic's Drive In expansion (unbuilt)</li>
      </ul>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Community Involvement</h2>
      <p>
        David has been a board member of <a href="https://www.springstreet.org/" target="_blank" rel="noopener noreferrer">Spring Street International School</a> since
        2003, where he oversaw construction of the dormitory, classrooms, and fine arts building
        and designed the multi-use facility planned for the school's next building phase.
      </p>
      <p>
        He is a founding member of the Friday Harbor Historic Preservation Review Board and developed
        conceptual plans for the San Juan Islands Agricultural Guild in 2008 with Washington State
        CTED funding.
      </p>
      <p>
        David led the local Cub Scout pack for 15 years &mdash; during that stretch, more than
        30% of the island's elementary-school boys were members. He received the American Legion
        Leadership Award (2013) from Post 163 for his long, dedicated service to Boy Scout Troop 90,
        and the BSA Council's Silver Beaver Award for distinguished service to youth. He was also a
        member of FANS, which raised funds for Island Rec, and organized the annual Children's
        Carnival.
      </p>

      <h2 style={{fontSize: '1.2rem', marginTop: '2rem'}}>Contact</h2>
      <p>
        <Link to="/contact">Contact David</Link> at his office at 310 Spring Street in Friday Harbor,
        by phone at (360) 378-3070, or by email
        at <a href="mailto:davidwaldron@interisland.net">davidwaldron@interisland.net</a>.
      </p>
    </Container>
    <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(PERSON_SCHEMA)}} />
  </Layout>
)

export default AboutPage
